import { Injectable, type Signal, inject, effect, signal, untracked } from "@angular/core";
import { FilterStore } from "./store/filter.store";
import { DashboardsService } from "@shared/dashboards-service";
import { VIEW_TYPE } from "./filter.tokens";
import type { FilterName, FilterField } from "./filter.types";
import { FilterRegistrationResolver } from "./filter-registration.resolver";
import { applicationTagFromApplicationType } from "@app/views-interfaces";

@Injectable()
export class FilterService {
    private readonly dashboardsService = inject(DashboardsService);
    private readonly filterRegistrationResolver = inject(FilterRegistrationResolver);
    public readonly viewType = inject(VIEW_TYPE);
    private readonly store = inject(FilterStore);

    /** Emits whenever the selected values of any registered filter change. */
    public readonly selections = this.store.selections;
    public readonly activeColumnFilterPopupId = signal<string | null>(null);

    public applyFilter<TItem>(data: Signal<TItem[]>): Signal<TItem[]> {
        return this.store.applyFilter<TItem>(data);
    }

    public getFilter(filterName: FilterName): FilterField | undefined {
        return this.store.get(filterName);
    }

    constructor() {
        effect(() => {
            const dashboardId = this.dashboardsService.currentId();
            if (!dashboardId) return;

            untracked(() => this.store.loadSelections(dashboardId));
        });

        effect(() => {
            const applicationType = this.dashboardsService.currentApplicationType();
            if (!applicationType) return;

            const applicationTag = applicationTagFromApplicationType(applicationType);
            if (applicationTag === "UNKNOWN") return;
            const filters = this.filterRegistrationResolver.get(applicationTag, this.viewType);

            untracked(() => this.store.setFilters(filters));
        });
    }
}
